// frontend/src/hooks/useLivestockPhotos.ts
/**
 * React Query hooks for one organism's photos on the compare page and in the
 * photo viewer (docs/plans/2026-10-29-livestock-compare-plan.md, 5.1). Every
 * change refreshes the organism's photos, its detail read (the reference and
 * its ghost) and its timeline (the feed's "Lined up" badges).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { livestockApi } from '@/api/livestock';
import { livestockPhotosApi, type LivestockReferenceResponse } from '@/api/livestockPhotos';
import type { Livestock } from '@/types/livestock';
import type { PhotoFraming, PhotoIndex } from '@/types/photos';
import type { OrganismPhoto } from '@/types/timeline';
import { livestockDetailKey, livestockPhotoKeys } from './livestockPhotoKeys';

/** A framing to save, or null to reset the photo's line up. */
export interface OrganismPhotoFramingChange {
  id: number;
  framing: PhotoFraming | null;
}

/**
 * Signed URLs outlive this by far; a refetch after it hands out fresh ones
 * before the old ones expire (as the tank shot hooks).
 */
const ORGANISM_PHOTO_STALE_MS = 30 * 60 * 1000;

/** A FastAPI 422 detail is a list, which the api client shows as "[object Object]". */
function messageOf(error: Error, fallback: string): string {
  return !error.message || error.message.startsWith('[object') ? fallback : error.message;
}

/** The organism's photos, its detail (reference) and its timeline (badges). */
export function invalidateOrganismPhotos(
  queryClient: QueryClient,
  tankId: string,
  livestockId: number
): void {
  void queryClient.invalidateQueries({ queryKey: livestockPhotoKeys.all(tankId, livestockId) });
  void queryClient.invalidateQueries({ queryKey: livestockDetailKey(tankId, livestockId) });
  void queryClient.invalidateQueries({ queryKey: ['timeline', tankId, livestockId] });
}

/** The organism (`useLivestockDetail`'s key and read, for a tank given by id). */
export function useOrganism(
  tankId: string | undefined,
  livestockId: number | undefined
): UseQueryResult<Livestock, Error> {
  return useQuery({
    queryKey: livestockDetailKey(tankId, livestockId),
    queryFn: () => livestockApi.get(tankId as string, livestockId as number),
    enabled: !!tankId && livestockId !== undefined,
  });
}

/** Every photo entry as (id, date, thumb, framing), oldest first. */
export function useOrganismPhotoIndex(
  tankId: string | undefined,
  livestockId: number | undefined
): UseQueryResult<PhotoIndex, Error> {
  return useQuery({
    queryKey: livestockPhotoKeys.index(tankId ?? '', livestockId ?? 0),
    queryFn: () => livestockPhotosApi.index(tankId as string, livestockId as number),
    enabled: !!tankId && livestockId !== undefined,
    staleTime: ORGANISM_PHOTO_STALE_MS,
  });
}

export function useOrganismPhoto(
  tankId: string | undefined,
  livestockId: number | undefined,
  id: number | undefined
): UseQueryResult<OrganismPhoto, Error> {
  return useQuery({
    queryKey: livestockPhotoKeys.one(tankId ?? '', livestockId ?? 0, id ?? 0),
    queryFn: () => livestockPhotosApi.get(tankId as string, livestockId as number, id as number),
    enabled: !!tankId && livestockId !== undefined && id !== undefined,
    staleTime: ORGANISM_PHOTO_STALE_MS,
  });
}

/** Line a photo up (a framing) or reset it (null). */
export function useSetOrganismPhotoFraming(
  tankId: string,
  livestockId: number
): UseMutationResult<OrganismPhoto, Error, OrganismPhotoFramingChange> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, framing }: OrganismPhotoFramingChange) =>
      framing
        ? livestockPhotosApi.setFraming(tankId, livestockId, id, framing)
        : livestockPhotosApi.clearFraming(tankId, livestockId, id),
    onSuccess: (_photo, { framing }) => {
      invalidateOrganismPhotos(queryClient, tankId, livestockId);
      notifications.show({ message: framing ? 'Lined up' : 'Line up reset', color: 'teal' });
    },
    onError: (error: Error, { framing }) => {
      notifications.show({
        title: framing ? "Line up wasn't saved" : "Line up wasn't reset",
        message: messageOf(error, 'Try again in a moment.'),
        color: 'red',
      });
    },
  });
}

/** An entry id makes that photo the organism's reference; null leaves it without one. */
export function useSetOrganismReference(
  tankId: string,
  livestockId: number
): UseMutationResult<LivestockReferenceResponse, Error, number | null> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: number | null) =>
      livestockPhotosApi.setReference(tankId, livestockId, entryId),
    onSuccess: (_result, entryId) => {
      invalidateOrganismPhotos(queryClient, tankId, livestockId);
      notifications.show({
        message: entryId === null ? 'Reference photo cleared' : 'Reference photo set',
        color: 'teal',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Reference photo wasn't changed",
        message: messageOf(error, 'Try again in a moment.'),
        color: 'red',
      });
    },
  });
}
